import type { OfferPageDispatchAction, OfferPageFilterType, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

import { last, without } from 'lib/array/arrayUtils'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'

function multiRoomsMobileFilterMiddleware(
  state: OfferPageState,
  action: OfferPageDispatchAction,
  next: (state: OfferPageState) => OfferPageState,
) {
  switch (action.type) {
    case OfferPageActions.checkAvailability:
    case OfferPageActions.toggleFilter:
      const nextState = next(state)
      if (state.activeFilter && action.filter !== state.activeFilter) {
        nextState.filterHistory = [...nextState.filterHistory, state.activeFilter]
      }
      return nextState
    case OfferPageActions.filterContinue: {
      const hasRooms = hasSelectedRooms(state.rooms)
      const hasDate = !!state.checkInDate
      const hasInvalidChildren = state.rooms?.some((room) =>
        room.childrenAge?.some((age) => age === -1),
      )
      /*
        Base filter flow is: Capacity -> Duration -> Check in -> Room -> Package option

        If there's no dates, we restart back at capacity
        Once we have dates, we go to room (or whatever's missing)
      */
      const nextState = next(state)
      let nextFilter: OfferPageFilterType | undefined = undefined
      if (hasInvalidChildren) {
        // invalid children, don't let them progress until dealt with
        nextFilter = 'capacity'
      } else if (state.activeFilter === 'capacity') {
        if (!hasRooms) {
          // user looked at capacity, didn't change anything and closed it
          // we consider this them applying the default rooms, so set rooms
          nextState.rooms = [generateOfferPageRoom()]
        }

        if (state.durationSelected) {
          nextFilter = 'checkin'
        } else {
          nextFilter = 'duration'
        }
      } else if (state.activeFilter === 'duration') {
        state.durationSelected = true
        nextFilter = 'checkin'
      } else if (state.activeFilter === 'checkin') {
        if (!hasRooms && hasDate) {
          nextFilter = 'capacity'
        } else if (hasDate && state.isMultiRoomSelectRooms) {
          nextState.isMultiRoomSelectRooms = false
          nextFilter = 'multiRoomSelect'
        }
      }

      if (state.activeFilter) {
        // push the next filter to the end
        nextState.filterHistory = [...nextState.filterHistory, state.activeFilter]
      }
      nextState.activeFilter = nextFilter
      return nextState
    }
    case OfferPageActions.filterBack: {
      const nextState = next(state)
      // 'pop' the last one (immutably!)
      nextState.activeFilter = last(state.filterHistory)
      nextState.filterHistory = without(state.filterHistory, nextState.activeFilter)
      return nextState
    }
    case OfferPageActions.closeFilter: {
      const nextState = next(state)
      // closed, restart the history
      nextState.filterHistory = []
      return nextState
    }
    default:
      return state
  }
}

export default multiRoomsMobileFilterMiddleware
